import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import { invalidateAuthorizedFile } from '@/utils/file-links';

import {
  mapCreateEducationToDto,
  mapCreateExperienceToDto,
  mapDtoToCrew,
  mapFiltersToDto,
  mapCreateUpdateProfileToDto,
} from './crew.mappers';
import type {
  CreateEducation,
  CreateExperience,
  CrewFilters,
  CrewList,
  CrewListDto,
  CrewSkill,
  CruisingExperience,
  SingleCrewDto,
  CreateUpdateProfile,
} from './crew.types';

export const getCrew = async (
  limit: number,
  page: number,
  filters: CrewFilters,
): Promise<CrewList> => {
  const { data } = await getRequest<CrewListDto>(API.PEOPLE, '/profiles', true, {
    limit,
    page,
    ...mapFiltersToDto(filters),
  });
  return {
    count: data.count,
    items: data.items.map(mapDtoToCrew),
  };
};

export const getSingleCrewByPersonId = async (personId: string) => {
  try {
    const { data } = await getRequest<SingleCrewDto>(API.PEOPLE, `/profiles/${personId}`, false);
    return mapDtoToCrew(data);
  } catch (err: unknown) {
    console.error(err);
    return null;
  }
};

export const updateProfileFavorite = async (id: string, isFavorite: boolean) => {
  if (isFavorite) {
    await postRequest(API.PEOPLE, `/profiles/${id}/like`, {});
  } else {
    await deleteRequest(API.PEOPLE, `/profiles/${id}/like`);
  }
};

export const createEducation = async (value: CreateEducation) => {
  await postRequest(API.PEOPLE, '/educations', mapCreateEducationToDto(value));
};

export const updateEducation = async (id: string, value: CreateEducation) => {
  await putRequest(API.PEOPLE, `/educations/${id}`, mapCreateEducationToDto(value));
};

export const deleteEducation = async (id: string) => {
  await deleteRequest(API.PEOPLE, `/educations/${id}`);
};

export const createProfile = async (values: CreateUpdateProfile) => {
  await postRequest(API.PEOPLE, `/profiles`, mapCreateUpdateProfileToDto(values));
};

export const updateProfile = async (values: CreateUpdateProfile) => {
  await putRequest(API.PEOPLE, `/profiles`, mapCreateUpdateProfileToDto(values));
};

export const getCruisingExperiences = async () => {
  const { data } = await getRequest<CruisingExperience[]>(API.PEOPLE, '/cruising-experiences');
  return data;
};

export const getCrewSkills = async () => {
  const { data } = await getRequest<CrewSkill[]>(API.PEOPLE, '/skills');
  return data;
};

export const _createExperience = async (values: CreateExperience) => {
  const { data } = await postRequest<{ id: string }, unknown>(
    API.PEOPLE,
    '/experiences',
    mapCreateExperienceToDto(values),
  );
  return data;
};

export const uploadFileToExperience = async (id: string, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  await patchRequest(API.PEOPLE, `/experiences/${id}/recommendation`, formData);
};

export const createExperience = async (values: CreateExperience) => {
  const { id } = await _createExperience(values);
  const file = values.file;
  if (file) {
    await uploadFileToExperience(id, file);
  }
};

export const _updateExperience = async (id: string, values: CreateExperience) => {
  const { data } = await putRequest<{ id: string }, unknown>(
    API.PEOPLE,
    `/experiences/${id}`,
    mapCreateExperienceToDto(values),
  );
  return data;
};

export const updateExperience = async (id: string, values: CreateExperience) => {
  await _updateExperience(id, values);
  const file = values.file;
  if (file) {
    await uploadFileToExperience(id, file);
  }
};

export const deleteExperience = async (id: string) => {
  await deleteRequest(API.PEOPLE, `/experiences/${id}`);
};

export const deleteProfile = async () => {
  await deleteRequest(API.PEOPLE, '/profiles');
};

export const uploadCv = async (cv: File) => {
  const formData = new FormData();
  formData.append('cv', cv);
  await patchRequest(API.PEOPLE, '/profiles/cv', formData);
};

export const deleteCv = async () => {
  await deleteRequest(API.PEOPLE, '/profiles/cv');
};

export const uploadAvatar = async (photo: File) => {
  const formData = new FormData();
  formData.append('photo', photo);
  await patchRequest(API.PEOPLE, '/profiles/photo', formData);
  invalidateAuthorizedFile();
};

export const createCruisingExperience = async (title: string) => {
  const { data } = await postRequest<{ id: string }, unknown>(API.PEOPLE, '/cruising-experiences', {
    title,
  });
  return data;
};
